import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PWAInstallButton from './PWAInstallButton';
import ProfileModal from './ProfileModal';
import ChangePasswordModal from './ChangePasswordModal';

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
  </svg>
);

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/login');
  };

  // Open-profile hook: any page can fire `edosar:open-profile` and the
  // navbar-hosted ProfileModal will pop. Lets the Dashboard surface a
  // "view mode" chip that jumps straight into the profile dialog
  // without us lifting modal state up the tree.
  useEffect(() => {
    const onOpenProfile = () => setProfileOpen(true);
    window.addEventListener('edosar:open-profile', onOpenProfile);
    return () => window.removeEventListener('edosar:open-profile', onOpenProfile);
  }, []);

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  const initials = user
    ? (user.full_name || user.email).split(' ').map(w => w[0]).join('').slice(0, 2)
    : '';

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <div className="navbar-start">
          <Link to="/dashboard" className="navbar-brand" onClick={handleCloseMenu}>
            <HeartIcon />
            <span>eDosar<strong>Medical</strong></span>
          </Link>
          <PWAInstallButton variant="navbar" />
        </div>

        <button
          type="button"
          className={`navbar-toggle${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(prev => !prev)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation"
        >
          <span />
          <span />
          <span />
        </button>

        <div className={`navbar-links${menuOpen ? ' open' : ''}`}>
          <Link
            to="/dashboard"
            className={`navbar-link${location.pathname === '/dashboard' ? ' active' : ''}`}
            onClick={handleCloseMenu}
          >
            Dashboard
          </Link>
          <Link
            to="/families"
            className={`navbar-link${location.pathname.startsWith('/families') ? ' active' : ''}`}
            onClick={handleCloseMenu}
          >
            Familii
          </Link>
          {user?.is_admin && (
            <Link
              to="/admin/users"
              className={`navbar-link${location.pathname.startsWith('/admin') ? ' active' : ''}`}
              onClick={handleCloseMenu}
            >
              Administrare
            </Link>
          )}
          {user && (
            <div className="navbar-user-menu">
              <button
                type="button"
                className="navbar-user-trigger"
                aria-haspopup="menu"
                aria-label="Meniu cont utilizator"
              >
                <span className="navbar-avatar">{initials}</span>
                <span className="navbar-username">{user.full_name || user.email}</span>
                <span className="navbar-user-chevron" aria-hidden>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </span>
              </button>
              <div className="navbar-user-dropdown" role="menu">
                <button
                  type="button"
                  className="navbar-dropdown-item"
                  role="menuitem"
                  onClick={() => {
                    setProfileOpen(true);
                    setMenuOpen(false);
                  }}
                >
                  Profil
                </button>
                <button
                  type="button"
                  className="navbar-dropdown-item"
                  role="menuitem"
                  onClick={() => {
                    setPasswordOpen(true);
                    setMenuOpen(false);
                  }}
                >
                  Parolă
                </button>
                <button type="button" className="navbar-dropdown-item navbar-dropdown-item--danger" role="menuitem" onClick={handleLogout}>
                  Deconectare
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
      <ChangePasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </nav>
  );
};

export default Navbar;
